import { useEffect, useMemo, useState } from "react";
import { trpc } from "../../lib/trpc.js";
import { Botao, Falha } from "../../components/ui.js";
import { PainelEtapa } from "./PainelEtapa.js";
import { EscolherFoto, type FotoEnviada } from "./EscolherFoto.js";
import { EtapaQuem, type Identificacao } from "./EtapaQuem.js";
import { EtapaAuxiliares, EtapaCapa } from "./EtapaBiblioteca.js";
import { EtapaPrompt } from "./EtapaPrompt.js";
import { EtapaGerar } from "./EtapaGerar.js";
import { EtapaEntrega } from "./EtapaEntrega.js";
import { EtapaSalvarFoto } from "./EtapaSalvarFoto.js";
import {
  anterior,
  etapasDoModo,
  oQueFaltaEmQuem,
  type Etapa,
  type ModoDoAtendimento,
} from "./etapas.js";
import { useGeracao } from "./useGeracao.js";

const VAZIO: Identificacao = {
  nome: "",
  vivenciaId: null,
  institutionId: null,
  participantId: null,
};

/**
 * O atendimento, do começo ao fim.
 *
 * A geração só nasce no banco quando há nome e foto — antes disso o estado vive
 * aqui, e desistir não deixa lixo. Depois disso o servidor é a fonte da verdade,
 * e esta tela só reflete.
 *
 * Dois modos (#95): com IA, o caminho de sempre; só fotografia, a foto sai como foi
 * tirada — quem é, a foto, salvar e entregar. Nesse modo a geração nasce já
 * publicada, no "Salvar foto".
 */
export function NovaGeracaoPage() {
  // Fica entre um atendimento e o próximo: quem fotografa várias pessoas seguidas não escolhe de novo.
  const [modo, setModo] = useState<ModoDoAtendimento>("ia");
  const [etapa, setEtapa] = useState<Etapa>("quem");
  const [quem, setQuem] = useState<Identificacao>(VAZIO);
  const [foto, setFoto] = useState<FotoEnviada | null>(null);
  const [auxiliares, setAuxiliares] = useState<string[]>([]);
  const [prompt, setPrompt] = useState("");
  const [capaId, setCapaId] = useState<number | null>(null);
  const [geracaoId, setGeracaoId] = useState<number | null>(null);
  // "Mostrar na galeria do salão" da foto sem IA: começa fora.
  const [naGaleriaDaFoto, setNaGaleriaDaFoto] = useState(false);
  // "Mostrar na galeria do salão" ao publicar com IA: por regra, toda foto entra (#96).
  const [naGaleriaAoPublicar, setNaGaleriaAoPublicar] = useState(true);
  // Depois de enviar, o próximo atendimento passa a ser o botão principal do rodapé.
  const [enviou, setEnviou] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const { geracao, processando, progresso } = useGeracao(geracaoId);
  const utils = trpc.useUtils();

  const criar = trpc.generations.create.useMutation({ onError: (e) => setErro(e.message) });
  const atualizar = trpc.generations.update.useMutation({ onError: (e) => setErro(e.message) });
  const gerar = trpc.generations.generateImage.useMutation({ onError: (e) => setErro(e.message) });
  const aprovar = trpc.generations.approveImage.useMutation({ onError: (e) => setErro(e.message) });
  const rejeitar = trpc.generations.rejectImage.useMutation({ onError: (e) => setErro(e.message) });
  const compor = trpc.generations.composeBookCover.useMutation({
    onError: (e) => setErro(e.message),
  });
  const finalizar = trpc.generations.finalize.useMutation({ onError: (e) => setErro(e.message) });
  const salvarFotografia = trpc.generations.salvarFotografia.useMutation({
    onError: (e) => setErro(e.message),
  });

  const vivencias = trpc.vivencias.listActive.useQuery();
  const nomeVivencia = vivencias.data?.find((v) => v.id === quem.vivenciaId)?.name;

  // O Local do "Só fotografia" (#101): só os ativos, e só vale o que ainda está na lista — um Local desativado
  // entre um atendimento e outro não segue escolhido às escondidas.
  const instituicoes = trpc.institutions.list.useQuery({});
  const locais = useMemo(
    () =>
      instituicoes.data
        ?.filter((l) => l.status === "active")
        .map((l) => ({ id: l.id, name: l.name })),
    [instituicoes.data],
  );
  const local = locais?.find((l) => l.id === quem.institutionId);
  // Com um Local só, não há o que escolher.
  const unicoLocal = locais?.length === 1 ? locais[0]!.id : null;
  useEffect(() => {
    if (modo === "fotografia" && unicoLocal && !quem.institutionId) {
      setQuem((q) => ({ ...q, institutionId: unicoLocal }));
    }
  }, [modo, unicoLocal, quem.institutionId]);

  function recomecar() {
    setEtapa("quem");
    // O Local fica, como o modo: quem fotografa várias pessoas seguidas no mesmo lugar não escolhe de novo.
    setQuem({ ...VAZIO, institutionId: quem.institutionId });
    setFoto(null);
    setAuxiliares([]);
    setPrompt("");
    setCapaId(null);
    setGeracaoId(null);
    setNaGaleriaDaFoto(false);
    setNaGaleriaAoPublicar(true);
    setEnviou(false);
    setErro(null);
  }

  async function avancarDaFoto() {
    if (!foto) return;
    setErro(null);

    // Só fotografia: nada nasce no banco até "Salvar foto" — desistir antes não deixa rastro.
    if (modo === "fotografia") {
      setEtapa("salvar");
      return;
    }

    // A geração nasce aqui: é o primeiro momento em que existe algo que vale
    // persistir. Antes disso, abandonar o atendimento não deixa rastro.
    if (geracaoId === null) {
      const criada = await criar.mutateAsync({
        spectatorName: quem.nome.trim(),
        spectatorPhotoPath: foto.path,
        participantId: quem.participantId ?? undefined,
        vivenciaId: quem.vivenciaId ?? undefined,
      });
      setGeracaoId(criada.id);
    } else {
      await atualizar.mutateAsync({ id: geracaoId, spectatorName: quem.nome.trim() });
    }

    setEtapa("auxiliares");
  }

  async function salvarEAvancar(destino: Etapa) {
    setErro(null);
    if (geracaoId !== null) {
      await atualizar.mutateAsync({
        id: geracaoId,
        auxImagePaths: auxiliares,
        imagePrompt: prompt || undefined,
      });
    }
    setEtapa(destino);
  }

  async function salvarAFoto() {
    if (!foto || !local) return;
    setErro(null);
    const salva = await salvarFotografia.mutateAsync({
      spectatorName: quem.nome.trim(),
      spectatorPhotoPath: foto.path,
      institutionId: local.id,
      ...(quem.participantId ? { participantId: quem.participantId } : {}),
      naGaleria: naGaleriaDaFoto,
    });
    setGeracaoId(salva.id);
    setEtapa("entrega");
  }

  const props = {
    etapa,
    etapas: etapasDoModo(modo),
    espectador: quem.nome || undefined,
    contexto: modo === "fotografia" ? local?.name : nomeVivencia,
    fotoUrl: geracao?.urls.generatedImage ?? foto?.url ?? null,
    capaUrl: geracao?.urls.composedImage ? null : undefined,
    // A foto sem IA não tem capa: inteira na moldura, em vez de meia moldura vazia esperando uma capa.
    compostoUrl:
      modo === "fotografia" ? (foto?.url ?? null) : (geracao?.urls.composedImage ?? null),
    estadoArtefato: processando
      ? ("processando" as const)
      : geracao?.imageStatus === "failed"
        ? ("falhou" as const)
        : geracao?.finalImagePath
          ? ("pronto" as const)
          : ("montando" as const),
    aoVoltar: anterior(etapa, modo) ? () => setEtapa(anterior(etapa, modo)!) : undefined,
  };

  const avisoErro = erro && <Falha mensagem={erro} tentarNovamente={() => setErro(null)} />;

  if (etapa === "quem") {
    const falta = oQueFaltaEmQuem(modo, { nome: quem.nome, institutionId: local?.id ?? null });

    return (
      <PainelEtapa
        {...props}
        aoVoltar={undefined}
        acao={
          <Botao tom="brasa" largo disabled={falta !== null} onClick={() => setEtapa("foto")}>
            {falta === "local" ? "Escolha o local para continuar" : "Continuar"}
          </Botao>
        }
      >
        {avisoErro}
        <EtapaQuem
          valor={quem}
          aoMudar={setQuem}
          modo={modo}
          aoMudarModo={geracaoId === null ? setModo : undefined}
          locais={locais}
        />
      </PainelEtapa>
    );
  }

  if (etapa === "foto") {
    return (
      <PainelEtapa
        {...props}
        acao={
          <Botao
            tom="brasa"
            largo
            disabled={!foto}
            carregando={criar.isPending || atualizar.isPending}
            onClick={() => void avancarDaFoto()}
          >
            Continuar
          </Botao>
        }
      >
        {avisoErro}
        <EscolherFoto fotoUrl={foto?.url} aoEnviar={setFoto} aoLimpar={() => setFoto(null)} />
      </PainelEtapa>
    );
  }

  if (etapa === "salvar" && foto) {
    return (
      <PainelEtapa
        {...props}
        acao={
          <Botao
            tom="brasa"
            largo
            carregando={salvarFotografia.isPending}
            onClick={() => void salvarAFoto().catch(() => undefined)}
          >
            Salvar foto
          </Botao>
        }
      >
        {avisoErro}
        <EtapaSalvarFoto
          fotoUrl={foto.url}
          naGaleria={naGaleriaDaFoto}
          aoMudarNaGaleria={setNaGaleriaDaFoto}
        />
      </PainelEtapa>
    );
  }

  if (etapa === "auxiliares") {
    return (
      <PainelEtapa
        {...props}
        acao={
          <Botao
            tom="brasa"
            largo
            carregando={atualizar.isPending}
            onClick={() => void salvarEAvancar("prompt")}
          >
            {auxiliares.length > 0
              ? `Continuar com ${auxiliares.length}`
              : "Seguir sem referências"}
          </Botao>
        }
      >
        {avisoErro}
        <EtapaAuxiliares selecionados={auxiliares} aoMudar={setAuxiliares} />
      </PainelEtapa>
    );
  }

  if (etapa === "prompt") {
    return (
      <PainelEtapa
        {...props}
        acao={
          <Botao
            tom="brasa"
            largo
            disabled={prompt.trim().length < 3}
            carregando={atualizar.isPending}
            onClick={() => void salvarEAvancar("gerar")}
          >
            Continuar
          </Botao>
        }
      >
        {avisoErro}
        <EtapaPrompt geracaoId={geracaoId} valor={prompt} aoMudar={setPrompt} />
      </PainelEtapa>
    );
  }

  if (etapa === "gerar" && geracaoId !== null) {
    const aprovada = geracao?.imageApproved === true;

    return (
      <PainelEtapa
        {...props}
        aoVoltar={processando ? undefined : props.aoVoltar}
        acao={
          aprovada ? (
            <Botao tom="brasa" largo onClick={() => setEtapa("capa")}>
              Continuar
            </Botao>
          ) : undefined
        }
      >
        {avisoErro}
        <EtapaGerar
          status={geracao?.imageStatus ?? "pending"}
          progresso={progresso}
          erro={geracao?.erro}
          erroTecnico={geracao?.erroTecnico}
          imagemUrl={geracao?.urls.generatedImage}
          aoGerar={() => gerar.mutate({ id: geracaoId, prompt })}
          aoRefazer={() =>
            rejeitar.mutate(
              { id: geracaoId },
              {
                // Mesma armadilha do compor: sem invalidar, a imagem recusada
                // continuaria na tela depois de já ter sido apagada no servidor.
                onSuccess: async () => {
                  await utils.generations.getById.invalidate({ id: geracaoId });
                  setEtapa("prompt");
                },
              },
            )
          }
          aoAprovar={() =>
            aprovar.mutate(
              { id: geracaoId },
              {
                onSuccess: () => {
                  void utils.generations.getById.invalidate({ id: geracaoId });
                  setEtapa("capa");
                },
              },
            )
          }
        />
      </PainelEtapa>
    );
  }

  if (etapa === "capa" && geracaoId !== null) {
    return (
      <PainelEtapa
        {...props}
        acao={
          <Botao
            tom="brasa"
            largo
            carregando={compor.isPending}
            onClick={() => {
              if (capaId === null) {
                setEtapa("entrega");
                return;
              }
              compor.mutate(
                { generationId: geracaoId, bookCoverId: capaId },
                {
                  // Sem invalidar, a etapa de entrega lê a geração em cache —
                  // ainda sem `composedImage` — e mostra a imagem da IA no
                  // lugar da composição que acabou de ser feita.
                  onSuccess: async () => {
                    await utils.generations.getById.invalidate({ id: geracaoId });
                    setEtapa("entrega");
                  },
                },
              );
            }}
          >
            {capaId === null ? "Seguir sem capa" : "Compor e continuar"}
          </Botao>
        }
      >
        {avisoErro}
        <EtapaCapa capaId={capaId} aoEscolher={setCapaId} />
      </PainelEtapa>
    );
  }

  if (etapa === "entrega" && geracaoId !== null) {
    const finalizada = Boolean(geracao?.finalImagePath);
    const temParticipante = Boolean(geracao?.participantId);
    // Enquanto há para onde enviar e ainda não foi, o envio é o principal na tela; o próximo atendimento, não.
    const enviarEPrincipal = !enviou && temParticipante;

    return (
      <PainelEtapa
        {...props}
        // Publicada, a foto não volta: "Voltar" levaria a refazer a capa, ou a salvar a foto sem IA de novo.
        aoVoltar={finalizada || modo === "fotografia" ? undefined : props.aoVoltar}
        acao={
          finalizada ? (
            <Botao tom={enviarEPrincipal ? "quieto" : "brasa"} largo onClick={recomecar}>
              Atender a próxima pessoa
            </Botao>
          ) : (
            <Botao
              tom="brasa"
              largo
              carregando={finalizar.isPending}
              onClick={() =>
                finalizar.mutate(
                  { id: geracaoId, naGaleria: naGaleriaAoPublicar },
                  { onSuccess: () => void utils.generations.getById.invalidate({ id: geracaoId }) },
                )
              }
            >
              Finalizar e publicar
            </Botao>
          )
        }
      >
        {avisoErro}
        <EtapaEntrega
          geracaoId={geracaoId}
          finalizada={finalizada}
          imagemUrl={
            geracao?.urls.finalImage ?? geracao?.urls.composedImage ?? geracao?.urls.generatedImage
          }
          nomeArquivo={geracao?.finalImagePath?.split("/").pop() ?? null}
          temParticipante={temParticipante}
          envio={{
            estado: geracao?.whatsappEstado ?? null,
            motivo: geracao?.whatsappErro ?? null,
            enviadaEm: geracao?.whatsappSentAt ?? null,
            enviadaPara: geracao?.whatsappSentTo ?? null,
          }}
          naGaleria={geracao?.naGaleria ?? true}
          naGaleriaAoPublicar={naGaleriaAoPublicar}
          aoMudarNaGaleriaAoPublicar={setNaGaleriaAoPublicar}
          aoEnviar={() => setEnviou(true)}
        />
      </PainelEtapa>
    );
  }

  // Estado impossível de alcançar pela navegação normal, mas alcançável por
  // recarregar a página no meio do fluxo.
  return (
    <PainelEtapa
      {...props}
      etapa="quem"
      acao={
        <Botao tom="brasa" largo onClick={recomecar}>
          Começar de novo
        </Botao>
      }
    >
      <p className="text-[0.9375rem] text-texto-suave">
        Este atendimento se perdeu ao recarregar a página. A biblioteca guarda tudo que já foi
        gerado.
      </p>
    </PainelEtapa>
  );
}
